import { fetchEventSource } from '@fortaine/fetch-event-source';
import { logger, sanitizeError } from '@heidi/runtime/logger';
import type {
  CreateExampleViaChatRequest,
  TemplateCreationRecordInitialGenerationRequest,
  UpdateStructureFromExampleRequest,
  UpdateStructureViaChatRequest,
} from '../../contracts';
import {
  TEMPLATE_CREATION_STREAM_WIRE_FORMAT_HEADER,
  TEMPLATE_CREATION_STREAM_WIRE_FORMAT_V1,
  templateStreamEnvelopeSchema,
  type StreamDeltaPayload,
} from './templateStreamEnvelope';
import { TemplateStructureStreamError } from './templateStructureStreamError';
import { getTemplateStreamTransport } from './transport';
import type { StreamType } from './types';

export const isTemplateStreamV1Negotiated = (): boolean =>
  !getTemplateStreamTransport().isOnPremRuntime();

function buildTemplateStreamHeaders(): Record<string, string> {
  const { buildScribeSseHeaders, getAuthTokenForHttpClientSync } =
    getTemplateStreamTransport();
  const token = getAuthTokenForHttpClientSync();
  // On-prem gateways allow only essential CORS request headers, so the v1
  // wire-format opt-in is omitted there — the backend then serves the legacy
  // `{data}` chunks, which templateStreamEnvelopeSchema still accepts.
  const templateStreamHeaders = isTemplateStreamV1Negotiated()
    ? {
        [TEMPLATE_CREATION_STREAM_WIRE_FORMAT_HEADER]:
          TEMPLATE_CREATION_STREAM_WIRE_FORMAT_V1,
      }
    : undefined;
  return buildScribeSseHeaders(token, templateStreamHeaders);
}

function dispatchTemplateStreamDataLine(
  rawMessage: string,
  handlers: {
    onMessage?: (streamData: StreamType) => void;
    onStreamErrorFrame?: (frame: { error: string; message?: string }) => void;
    onDone?: () => void;
    onUnparseableFrame?: (rawMessage: string) => void;
  },
  logPrefix: string
): void {
  if (!rawMessage.trim()) {
    return;
  }
  const streamParseResult = templateStreamEnvelopeSchema.safeParse(rawMessage);
  if (!streamParseResult.success) {
    // PHI: `rawMessage` is the verbatim SSE frame and carries generated
    // template structure and example-note clinical text, and a zod error
    // embeds the received value in its issue messages. Log the shape instead —
    // an issue's `path` and `code` are schema vocabulary, and a length is a
    // count (`.ai-context/telemetry-phi-pii-safety.md`: length and shape,
    // never the value). The unparsed frame still reaches the in-app handler
    // below, so nothing the caller observes changes.
    logger.error(`${logPrefix} stream parse error`, {
      issues: streamParseResult.error.issues.map((issue) => ({
        path: issue.path.join('.'),
        code: issue.code,
      })),
      rawMessageLength: rawMessage.length,
    });
    handlers.onUnparseableFrame?.(rawMessage);
    return;
  }
  const data = streamParseResult.data;
  try {
    if ('kind' in data) {
      if (data.kind === 'error') {
        handlers.onStreamErrorFrame?.({
          error: data.error,
          message: data.message,
        });
        return;
      }
      if (data.kind === 'done') {
        handlers.onDone?.();
        return;
      }
      const payload: StreamDeltaPayload = { data: data.text };
      handlers.onMessage?.(payload);
      return;
    }
    const payload: StreamDeltaPayload = { data: data.data };
    handlers.onMessage?.(payload);
  } catch (error) {
    if (error instanceof TemplateStructureStreamError) {
      throw error;
    }
    logger.error(`${logPrefix} stream message handler error`, error);
  }
}

export interface TemplateStreamEventHandlers {
  onOpen?: (response: Response) => Promise<void> | void;
  onMessage?: (streamData: StreamType) => void;
  /** Structured error frame (Option C); not used for all stream entrypoints */
  onStreamErrorFrame?: (frame: { error: string; message?: string }) => void;
  /** Terminal v1 `done` frame. Legacy `{data}` streams never emit this. */
  onDone?: () => void;
  /** Counted by conversion completeness; other consumers may ignore it. */
  onUnparseableFrame?: (rawMessage: string) => void;
  onClose?: () => void;
  onError?: (error: unknown) => void;
}

function streamTemplateGeneration(
  request: {
    url: string;
    data?: object;
    signal: AbortSignal;
    logPrefix: string;
    templateCreationRecordId: string;
  },
  handlers: TemplateStreamEventHandlers
): Promise<void> {
  const { signal, logPrefix } = request;
  if (signal.aborted) return Promise.resolve();
  const requestHeaders = buildTemplateStreamHeaders();

  // A done frame completes the operation even if the transport never sends
  // EOF. Keep its controller separate so the caller can start the example.
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener('abort', abort, { once: true });
  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    logger.debug(`${logPrefix} stream closed`);
    try {
      handlers.onClose?.();
    } catch (error) {
      logger.error(`${logPrefix} onClose handler error`, sanitizeError(error));
    }
  };
  const dispatchHandlers = {
    ...handlers,
    onDone: () => {
      try {
        handlers.onDone?.();
      } finally {
        close();
        controller.abort();
      }
    },
  };

  return fetchEventSource(request.url, {
    headers: requestHeaders,
    openWhenHidden: true,
    method: 'POST',
    ...(request.data ? { body: JSON.stringify(request.data) } : {}),
    signal: controller.signal,
    onopen: (response) =>
      getTemplateStreamTransport().handleStreamOpen({
        response,
        logPrefix,
        onOpen: handlers.onOpen,
        onError: handlers.onError,
        context: { templateCreationRecordId: request.templateCreationRecordId },
      }),
    onmessage(ev) {
      if (closed || controller.signal.aborted) return;
      dispatchTemplateStreamDataLine(ev.data, dispatchHandlers, logPrefix);
    },
    onclose: close,
    onerror(err) {
      logger.error(`${logPrefix} stream error`, sanitizeError(err));
      try {
        handlers.onError?.(err as unknown);
      } catch (error) {
        logger.error(`${logPrefix} onError handler error`, sanitizeError(error));
      }
      throw err; // Stop the event-source library from retrying a POST.
    },
  }).finally(() => signal.removeEventListener('abort', abort));
}

export interface TemplateStreamConfig {
  data: TemplateCreationRecordInitialGenerationRequest;
  templateCreationRecordId: string;
  targetVersionControlId: string;
  signal: AbortSignal;
  logPrefix?: string;
}

/**
 * Streams a template creation request and handles the response
 * @param config - Configuration for the streaming request (must include signal for abort control)
 * @param handlers - Event handlers for stream events
 */
export function streamInitialStructureGeneration(
  config: TemplateStreamConfig,
  handlers: TemplateStreamEventHandlers = {}
): Promise<void> {
  return streamTemplateGeneration(
    {
      ...config,
      url: buildInitialStructureGenerationRequestPath(config),
      logPrefix: config.logPrefix ?? '[TEMPLATE_CREATION_STREAM]',
    },
    handlers
  );
}

export const buildInitialStructureGenerationRequestPath = ({
  templateCreationRecordId,
  targetVersionControlId,
}: {
  templateCreationRecordId: string;
  targetVersionControlId: string;
}) => {
  return `${getTemplateStreamTransport().getDomain('v2')}templates/template-creation-record/${templateCreationRecordId}/version-control/${targetVersionControlId}/generate-initial`;
};

export interface GenerateExampleFromStructureRequest {
  template_creation_record_id: string;
  version_control_id: string;
}

export interface ExampleNoteStreamConfig {
  data: GenerateExampleFromStructureRequest;
  signal: AbortSignal;
  logPrefix?: string;
}

/**
 * Streams an example note generation request and handles the response
 * @param config - Configuration for the streaming request (must include signal for abort control)
 * @param handlers - Event handlers for stream events
 */
export function streamExampleFromStructureGeneration(
  config: ExampleNoteStreamConfig,
  handlers: TemplateStreamEventHandlers = {}
): Promise<void> {
  return streamTemplateGeneration(
    {
      url: buildExampleFromStructureRequestPath(
        config.data.template_creation_record_id,
        config.data.version_control_id
      ),
      signal: config.signal,
      logPrefix: config.logPrefix ?? '[EXAMPLE_NOTE_STREAM]',
      templateCreationRecordId: config.data.template_creation_record_id,
    },
    handlers
  );
}

export const buildExampleFromStructureRequestPath = (
  templateCreationRecordId: string,
  versionControlId: string
) => {
  return `${getTemplateStreamTransport().getDomain('v2')}templates/template-creation-record/${templateCreationRecordId}/${versionControlId}/example-note`;
};

export interface StructureViaChatStreamConfig {
  data: UpdateStructureViaChatRequest;
  templateCreationRecordId: string;
  targetVersionControlId: string;
  signal: AbortSignal;
  logPrefix?: string;
}

/**
 * Streams a structure generation via chat request and handles the response
 * @param config - Configuration for the streaming request (must include signal for abort control)
 * @param handlers - Event handlers for stream events
 */
export function streamStructureViaChatGeneration(
  config: StructureViaChatStreamConfig,
  handlers: TemplateStreamEventHandlers = {}
): Promise<void> {
  return streamTemplateGeneration(
    {
      ...config,
      url: buildStructureViaChatRequestPath(config),
      logPrefix: config.logPrefix ?? '[STRUCTURE_VIA_CHAT_STREAM]',
    },
    handlers
  );
}

export const buildStructureViaChatRequestPath = ({
  templateCreationRecordId,
  targetVersionControlId,
}: {
  templateCreationRecordId: string;
  targetVersionControlId: string;
}) => {
  return `${getTemplateStreamTransport().getDomain('v2')}templates/template-creation-record/${templateCreationRecordId}/version-control/${targetVersionControlId}/chat/structure`;
};

export interface ExampleViaChatStreamConfig {
  data: CreateExampleViaChatRequest;
  templateCreationRecordId: string;
  targetVersionControlId: string;
  signal: AbortSignal;
  logPrefix?: string;
}

/**
 * Streams an example update via chat request and handles the response
 * @param config - Configuration for the streaming request (must include signal for abort control)
 * @param handlers - Event handlers for stream events
 */
export function streamExampleViaChatGeneration(
  config: ExampleViaChatStreamConfig,
  handlers: TemplateStreamEventHandlers = {}
): Promise<void> {
  return streamTemplateGeneration(
    {
      ...config,
      url: buildExampleViaChatRequestPath(config),
      logPrefix: config.logPrefix ?? '[EXAMPLE_VIA_CHAT_STREAM]',
    },
    handlers
  );
}

export const buildExampleViaChatRequestPath = ({
  templateCreationRecordId,
  targetVersionControlId,
}: {
  templateCreationRecordId: string;
  targetVersionControlId: string;
}) => {
  return `${getTemplateStreamTransport().getDomain('v2')}templates/template-creation-record/${templateCreationRecordId}/version-control/${targetVersionControlId}/chat/example-note`;
};

export interface UpdateStructureFromNoteStreamConfig {
  data: UpdateStructureFromExampleRequest;
  templateCreationRecordId: string;
  targetVersionControlId: string;
  signal: AbortSignal;
  logPrefix?: string;
}

/**
 * Streams a structure update from note request and handles the response
 * @param config - Configuration for the streaming request (must include signal for abort control)
 * @param handlers - Event handlers for stream events
 */
export function streamUpdateStructureFromNote(
  config: UpdateStructureFromNoteStreamConfig,
  handlers: TemplateStreamEventHandlers = {}
): Promise<void> {
  return streamTemplateGeneration(
    {
      ...config,
      url: buildUpdateStructureFromNoteRequestPath(config),
      logPrefix: config.logPrefix ?? '[UPDATE_STRUCTURE_FROM_NOTE_STREAM]',
    },
    handlers
  );
}

export const buildUpdateStructureFromNoteRequestPath = ({
  templateCreationRecordId,
  targetVersionControlId,
}: {
  templateCreationRecordId: string;
  targetVersionControlId: string;
}) => {
  return `${getTemplateStreamTransport().getDomain('v2')}templates/template-creation-record/${templateCreationRecordId}/version-control/${targetVersionControlId}/example-note/structure`;
};
